import { createSelector } from 'reselect';
import { sortUsersByCurrentFirst, calcPercent, getObjValue } from 'utils/functions';
import { StatusTypes } from 'utils/constants';
import { getCurrentUser } from 'selectors/user/selector';
import { getFormattedName, FIRST_LAST } from 'selectors/formatName/selector';

const getIdFromUrlParams = props => getObjValue(props, 'match.params.id', null);
const getAssessment = props => getObjValue(props, 'assessment', null);
const getFields = props => getObjValue(props, 'assessment.fields', []);
const getFieldActions = props => getObjValue(props, 'actions.fieldActions', []);
const getSectionActions = props => getObjValue(props, 'actions.sectionActions', []);
const getFieldsFilter = props => getObjValue(props, 'fieldsFilter', null);

const isAssessmentSaving = props => {
  const arr = getObjValue(props, 'listFieldsState.listFieldsState', []);
  return !!arr.find(i => i.loading);
};

const isAssessmentSubmitted = status =>
  [StatusTypes.SUBMITTED, StatusTypes.SUBMITTED_SYNC_SUCCESS, StatusTypes.SUBMITTED_SYNC_FAILED].includes(status);

const getAssessmentOwners = createSelector([getAssessment], assessment => getObjValue(assessment, 'owners', []));

const getAssessmentParticipants = createSelector([getAssessment], assessment => getObjValue(assessment, 'participants', []));

const getAssessmentParticipantsMetadata = createSelector([getAssessment], assessment => getObjValue(assessment, 'metadata.participants', []));

const getAssessmentCategories = createSelector([getAssessment], assessment => getObjValue(assessment, 'categories', []));

const getAssessmentQuestions = createSelector([getAssessment], assessment => getObjValue(assessment, 'fields', []));

const isAssessmentReadOnly = createSelector([getAssessment], assessment => {
  return [StatusTypes.SUBMITTED, StatusTypes.SUBMITTED_SYNC_SUCCESS, StatusTypes.SUBMITTED_SYNC_FAILED].includes(
    getObjValue(assessment, 'status', 'INCOMPLETE')
  );
});

const getAssessmentStatus = createSelector([getAssessment], assessment => {
  let status;
  if (assessment.status === StatusTypes.SUBMITTED) {
    status = 'SUBMITTED';
  } else if (assessment.status === StatusTypes.SUBMITTED_SYNC_SUCCESS) {
    status = 'SUBMITTED_SYNC_SUCCESS';
  } else if (assessment.status === StatusTypes.SUBMITTED_SYNC_FAILED) {
    status = 'SUBMITTED_SYNC_FAILED';
  } else if (assessment.status === StatusTypes.NEW) {
    status = 'NEW';
  } else if (assessment.metadata.answeredFields === assessment.metadata.totalFields) {
    status = 'COMPLETED';
  } else {
    status = 'IN_PROGRESS';
  }
  return status;
});

const getCalculatedFields = createSelector([getAssessment, getFields], (assessment, fields) => {
  let calculatedFields = [];
  if (assessment) {
    calculatedFields = fields?.filter(field => field.calculated === true);
  }

  return calculatedFields;
});

const getAssessmentHiddenFields = createSelector(
  [getAssessment, getFields, getAssessmentCategories, getFieldActions, getSectionActions],
  (assessment, fields, categories, fieldActions, sectionActions) => {
    if (assessment) {
      let hiddenFieldsList = [];
      const hiddenSections = sectionActions?.filter(section => section.acl_action === 'Hide');
      const hiddenSectionListFromCategories = categories?.filter(x => hiddenSections.some(y => y.id === x.id));
      const fieldsExtractedFromHiddenSections = fields?.filter(x => hiddenSectionListFromCategories.some(y => y.id === x.categoryId));
      const hiddenFieldListFromAction = fieldActions?.filter(field => field.acl_action === 'Hide');
      const hiddenFieldListFromFieldsList = fields?.filter(x => hiddenFieldListFromAction.some(y => y.id === x.id));
      if (hiddenFieldListFromFieldsList) {
        const ids = new Set(hiddenFieldListFromFieldsList.map(d => d.id));
        hiddenFieldsList = [...hiddenFieldListFromFieldsList, ...fieldsExtractedFromHiddenSections?.filter(d => !ids?.has(d.id))];
      }
      return hiddenFieldsList;
    }
    return [];
  }
);

const getAssessmentReadonlyFields = createSelector(
  [getAssessment, getFields, getAssessmentCategories, getFieldActions, getSectionActions],
  (assessment, fields, categories, fieldActions, sectionActions) => {
    if (assessment) {
      let readOnlyFieldsList = [];
      const readOnlySections = sectionActions?.filter(section => section.acl_action === 'ReadOnly');
      const readOnlySectionListFromCategories = categories?.filter(x => readOnlySections.some(y => y.id === x.id));
      const fieldsExtractedFromreadOnlySections = fields?.filter(x => readOnlySectionListFromCategories.some(y => y.id === x.categoryId));
      const readOnlyFieldListFromAction = fieldActions?.filter(field => field.acl_action === 'ReadOnly');
      const readOnlyFieldListFromFieldsList = fields?.filter(x => readOnlyFieldListFromAction.some(y => y.id === x.id));
      if (readOnlyFieldListFromFieldsList) {
        const ids = new Set(readOnlyFieldListFromFieldsList.map(d => d.id));
        readOnlyFieldsList = [...readOnlyFieldListFromFieldsList, ...fieldsExtractedFromreadOnlySections?.filter(d => !ids?.has(d.id))];
      }
      return readOnlyFieldsList;
    }
    return [];
  }
);

const getAssessmentAnsweredQuestions = createSelector(
  [getAssessment, getAssessmentHiddenFields, getAssessmentReadonlyFields, getCalculatedFields],
  (assessment, hiddenFieldsList, readOnlyFieldsList, calculatedFields) => {
    let answered = 0;
    let ddeFieldsList = [];
    if (assessment) {
      ddeFieldsList = [...readOnlyFieldsList, ...hiddenFieldsList];
      const answeredHiddenOrReadonly = ddeFieldsList?.filter(field => field.answered === true);
      const answeredHiddenOrReadonlyCalculatedFields = answeredHiddenOrReadonly?.filter(x => calculatedFields?.some(y => y.id === x.id)).length;
      /* Checking whether answeredHiddenOrReadonly fields are calculated fields or not. If yes, not filtering those fields again since it has already filtered in the backend */
      const assessmentAnsweredFields =
        answeredHiddenOrReadonlyCalculatedFields > 0
          ? assessment.metadata.answeredFields + answeredHiddenOrReadonlyCalculatedFields
          : assessment.metadata.answeredFields;
      const answeredHiddenOrReadonlyLength = answeredHiddenOrReadonly?.length;
      const answeredFieldsForProgress = assessmentAnsweredFields > 0 ? assessmentAnsweredFields - answeredHiddenOrReadonlyLength : 0;
      if (Number(answeredFieldsForProgress)) {
        answered = answeredFieldsForProgress;
      }
    }
    return answered;
  }
);

const getAssessmentTotalQuestions = createSelector(
  [getAssessment, getAssessmentHiddenFields, getAssessmentReadonlyFields, getCalculatedFields],
  (assessment, hiddenFieldsList, readOnlyFieldsList, calculatedFields) => {
    let total = 0;
    let ddeFieldsList = [];
    if (assessment) {
      ddeFieldsList = [...hiddenFieldsList, ...readOnlyFieldsList];
      const readOnlyOrHiddenCalculatedFields = ddeFieldsList?.filter(x => calculatedFields?.some(y => y.id === x.id)).length;
      /* Checking whether readOnlyOrHidden fields are calculated fields or not. If yes, not filtering those fields again since it has already filtered in the backend */
      const assessmentTotalFields =
        readOnlyOrHiddenCalculatedFields > 0 ? assessment.metadata.totalFields + readOnlyOrHiddenCalculatedFields : assessment.metadata.totalFields;
      const totalDdeFieldLength = ddeFieldsList?.length;
      const totalFieldsForProgress = assessmentTotalFields - totalDdeFieldLength;

      if (Number(totalFieldsForProgress)) {
        total = totalFieldsForProgress;
      }
    }
    return total;
  }
);

const getAssessmentProgress = createSelector(
  [getAssessment, getAssessmentHiddenFields, getAssessmentReadonlyFields, getCalculatedFields],
  (assessment, hiddenFieldsList, readOnlyFieldsList, calculatedFields) => {
    const progress = 0;
    let totalDdeFieldLength = 0;
    let answeredHiddenOrReadonly = 0;
    let ddeFieldsList = [];
    if (assessment) {
      ddeFieldsList = [...readOnlyFieldsList, ...hiddenFieldsList];
      const readOnlyOrHiddenCalculatedFields = ddeFieldsList?.filter(x => calculatedFields?.some(y => y.id === x.id)).length;
      /* Checking whether readOnlyOrHidden fields are calculated fields or not. If yes, not filtering those fields again since it has already filtered in the backend */
      const assessmentTotalFields = readOnlyOrHiddenCalculatedFields
        ? assessment.metadata.totalFields + readOnlyOrHiddenCalculatedFields
        : assessment.metadata.totalFields;
      totalDdeFieldLength = ddeFieldsList?.length;
      const total = assessmentTotalFields - totalDdeFieldLength;

      answeredHiddenOrReadonly = ddeFieldsList?.filter(field => field.answered === true);
      const answeredHiddenOrReadonlyCalculatedFields = answeredHiddenOrReadonly?.filter(x => calculatedFields?.some(y => y.id === x.id)).length;
      /* Checking whether answeredHiddenOrReadonly fields are calculated fields or not. If yes, not filtering those fields again since it has already filtered in the backend */
      const assessmentAnsweredFields =
        answeredHiddenOrReadonlyCalculatedFields > 0
          ? assessment.metadata.answeredFields + answeredHiddenOrReadonlyCalculatedFields
          : assessment.metadata.answeredFields;
      const answeredHiddenOrReadonlyLength = answeredHiddenOrReadonly?.length;

      if (Number(total)) {
        const answered = assessmentAnsweredFields > 0 ? assessmentAnsweredFields - answeredHiddenOrReadonlyLength : 0;
        return calcPercent(answered, total);
      }
    }
    return progress;
  }
);

const getParticipantHiddenOrReadonlyFieldsMap = createSelector(
  [getAssessment, getAssessmentHiddenFields, getAssessmentParticipants, getAssessmentReadonlyFields],
  (assessment, hiddenFieldsList, participants, readOnlyFieldsList) => {
    let ddeFieldsList = [];
    let totalHiddenOrReadonlyFields = [];
    let totalAnsweredHiddenOrReadonlyFields = [];
    ddeFieldsList = [...hiddenFieldsList, ...readOnlyFieldsList];
    if (assessment && ddeFieldsList) {
      const data = participants.map(participant => {
        const fieldsAssigned = participant?.fields;
        const email = participant?.email;
        const hiddenOrReadonlyFields = ddeFieldsList?.filter(x => fieldsAssigned?.some(y => y.id === x.id));
        totalHiddenOrReadonlyFields = hiddenOrReadonlyFields;
        totalAnsweredHiddenOrReadonlyFields = hiddenOrReadonlyFields?.filter(field => field.answered === true);
        const hiddenOrReadonlyFieldsMetaData = {
          email,
          totalAnsweredHiddenOrReadonlyFields,
          totalHiddenOrReadonlyFields
        };
        return hiddenOrReadonlyFieldsMetaData;
      });
      return data;
    }
    return [];
  }
);

const getAssessmentParticipantTableData = createSelector(
  [
    getCurrentUser,
    getAssessmentParticipants,
    getAssessmentParticipantsMetadata,
    getAssessmentOwners,
    getParticipantHiddenOrReadonlyFieldsMap,
    getCalculatedFields
  ],
  (currentUser, participants, participantsMetadata, assessmentOwners, hiddenOrReadonlyFieldsMetaData, calculatedFields) => {
    const sortedParticipants = [...participants].sort(sortUsersByCurrentFirst(currentUser));
    const data = sortedParticipants.map(participant => {
      let answeredHiddenOrReadonlyFieldsLength = 0;
      let totalHiddenOrReadonlyFieldsLength = 0;
      const metadata = participantsMetadata.find(meta => meta.email === participant.email) || {};
      const isOwner = assessmentOwners.findIndex(owner => participant.email === owner.email) !== -1;
      const participantHiddenFieldsOrReadOnlyMetadata = hiddenOrReadonlyFieldsMetaData?.find(meta => meta.email === participant.email) || {};
      const fullName = getFormattedName({ user: participant, format: FIRST_LAST });

      /* Checking whether answeredHiddenOrReadonly fields are calculated fields or not. If yes, not filtering those fields again since it has already filtered in the backend */
      const answeredHiddenOrReadonlyFields = participantHiddenFieldsOrReadOnlyMetadata?.totalAnsweredHiddenOrReadonlyFields;
      answeredHiddenOrReadonlyFieldsLength = answeredHiddenOrReadonlyFields?.length;
      const answeredHiddenOrReadonlyCalculatedFields = answeredHiddenOrReadonlyFields?.filter(x => calculatedFields?.some(y => y.id === x.id)).length;
      const answeredFieldsForProgress =
        answeredHiddenOrReadonlyCalculatedFields > 0 ? metadata.answeredFields + answeredHiddenOrReadonlyCalculatedFields : metadata.answeredFields;

      /* Checking whether readOnlyOrHidden fields are calculated fields or not. If yes, not filtering those fields again since it has already filtered in the backend */
      const totalHiddenOrReadonlyFields = participantHiddenFieldsOrReadOnlyMetadata?.totalHiddenOrReadonlyFields;
      totalHiddenOrReadonlyFieldsLength = totalHiddenOrReadonlyFields?.length;
      const totalHiddenOrReadonlyCalculatedFields = totalHiddenOrReadonlyFields?.filter(x => calculatedFields?.some(y => y.id === x.id)).length;
      const totalFieldsForProgress =
        totalHiddenOrReadonlyCalculatedFields > 0 ? metadata.totalFields + totalHiddenOrReadonlyCalculatedFields : metadata.totalFields;
      const total = totalFieldsForProgress - totalHiddenOrReadonlyFieldsLength;
      const answered = answeredFieldsForProgress > 0 ? answeredFieldsForProgress - answeredHiddenOrReadonlyFieldsLength : 0;
      let complete = 0;

      if (Number(total)) {
        complete = calcPercent(answered, total);
      }

      const user = {
        ...participant,
        ...metadata,
        isOwner,
        fullName,
        complete,
        answered,
        total
      };
      return user;
    });

    return data;
  }
);

const getDDERequiredFields = createSelector(
  [getAssessment, getFields, getFieldActions, getSectionActions],
  (assessment, fields, fieldActions, sectionActions) => {
    if (assessment) {
      const hiddenAndReadOnlyFields = fields?.filter(field =>
        sectionActions?.some(action => action.id === field.categoryId && (action.acl_action === 'Hide' || action.acl_action === 'ReadOnly'))
      );

      const requiredFieldsFromActions = fields?.filter(field =>
        fieldActions?.some(action => action.id === field.id && action.acl_action === 'Required')
      );

      const DDERequiredFields = requiredFieldsFromActions?.filter(field => !hiddenAndReadOnlyFields?.some(f => field.id === f.id));

      return DDERequiredFields;
    }
    return [];
  }
);

const getActiveUsersEmails = (activeUsers, currentUser) => {
  if (activeUsers && activeUsers.activeUsers && activeUsers.activeUsers.length > 0) {
    const activeUsersFromList = activeUsers.activeUsers.map(user => user.email);
    if (currentUser && currentUser.getCurrentUser && !activeUsersFromList.find(activeUser => activeUser === currentUser.getCurrentUser.email)) {
      activeUsersFromList.push(currentUser.getCurrentUser.email);
    }
    return activeUsersFromList;
  }

  return [];
};

const getUpdatedCategoryListAfterFilter = createSelector(
  [getFields, getAssessmentCategories, getAssessmentParticipants, getFieldsFilter],
  (fields, categories, participants, fieldsFilter) => {
    let finalCategories = [];
    let fieldsArray = [];
    let categoriesFromQuestions = [];
    const { assignees, unassigned, required, answered, comments, ddeRequiredFields } = fieldsFilter;

    function checkHasComments(fieldsArg) {
      return !!fieldsArg.commentsCount;
    }

    function checkIsRequired(fieldsArg) {
      return fieldsArg.required.toString() === required || fieldsArg.requiredFromOtherText === true;
    }

    function checkIsRequiredByDDE(fieldsArg) {
      return ddeRequiredFields?.length > 0 && ddeRequiredFields?.some(y => y === fieldsArg?.id);
    }

    function checkIsAnswered(fieldsArg) {
      const isComplete = fieldsArg.requiredFromOtherText ? fieldsArg.otherText : fieldsArg.answered;
      const isIncomplete = fieldsArg.requiredFromOtherText ? !fieldsArg.otherText : !fieldsArg.answered;
      return answered === 'true' ? isComplete : isIncomplete;
    }

    function getNumberOfQuestionFiltersApplied() {
      const appliedFiltersArray = Object.entries({ required, answered, comments });
      const noOfQuestionFiltersApplied = appliedFiltersArray.filter(([, value]) => !Object.is(value, null)).length;
      return noOfQuestionFiltersApplied;
    }

    function applyAllQuestionFilters(fieldsArg) {
      return categories?.filter(x =>
        fieldsArg?.some(y => y.categoryId === x.id && (checkIsRequired(y) || checkIsRequiredByDDE(y)) && checkIsAnswered(y) && checkHasComments(y))
      );
    }

    function applyTwoQuestionFilters(fieldsArg) {
      return categories?.filter(x =>
        fieldsArg?.some(y => {
          let filterCondition;
          if (required && answered) {
            filterCondition = (checkIsRequired(y) || checkIsRequiredByDDE(y)) && checkIsAnswered(y);
          } else if (required && comments) {
            filterCondition = (checkIsRequired(y) || checkIsRequiredByDDE(y)) && checkHasComments(y);
          } else if (answered && comments) {
            filterCondition = checkIsAnswered(y) && checkHasComments(y);
          }
          return y.categoryId === x.id && filterCondition;
        })
      );
    }

    function applyOneQuestionFilter(fieldsArg) {
      return categories?.filter(x =>
        fieldsArg?.some(y => {
          let filterCondition;
          if (required) {
            filterCondition = checkIsRequired(y) || checkIsRequiredByDDE(y);
          } else if (answered) {
            filterCondition = checkIsAnswered(y);
          } else if (comments) {
            filterCondition = checkHasComments(y);
          }
          return y.categoryId === x.id && filterCondition;
        })
      );
    }

    function applyQuestionFilters(fieldsArg) {
      const noOfQuestionFiltersApplied = getNumberOfQuestionFiltersApplied();
      let filteredCategories = [];
      if (noOfQuestionFiltersApplied === 1) {
        filteredCategories = applyOneQuestionFilter(fieldsArg);
      } else if (noOfQuestionFiltersApplied === 2) {
        filteredCategories = applyTwoQuestionFilters(fieldsArg);
      } else if (noOfQuestionFiltersApplied === 3) {
        filteredCategories = applyAllQuestionFilters(fieldsArg);
      }
      return filteredCategories;
    }

    function getCategoriesFromQuestions(updatedFields) {
      if (required !== null || answered !== null || comments !== null) {
        categoriesFromQuestions = categoriesFromQuestions.concat(applyQuestionFilters(updatedFields));
      }
      fieldsArray = fieldsArray.concat(updatedFields);
    }

    function getUpdatedFields(item) {
      const updatedFields = fields?.filter(x => participants?.some(y => y.fields.some(z => z.id === x.id && y.email === item)));
      getCategoriesFromQuestions(updatedFields);
    }

    if (assignees.length > 0 || unassigned.toString() === 'true') {
      if (unassigned.toString() === 'true') {
        const updatedFields = fields?.filter(x => !participants?.some(y => y.fields.some(z => z.id === x.id)));
        getCategoriesFromQuestions(updatedFields);
      }

      if (assignees.length > 0) {
        assignees.forEach(getUpdatedFields);
      }
      const uniqueCategoryIds = [...new Set([...new Set(fieldsArray)].map(x => x.categoryId))];
      finalCategories = categories?.filter(x => uniqueCategoryIds.some(y => y === x.id));
      if (categoriesFromQuestions && getNumberOfQuestionFiltersApplied() > 0) {
        finalCategories = finalCategories.filter(value => categoriesFromQuestions.includes(value));
      }
    } else {
      finalCategories = applyQuestionFilters(fields);
    }

    return finalCategories;
  }
);

export {
  isAssessmentSaving,
  isAssessmentReadOnly,
  isAssessmentSubmitted,
  getIdFromUrlParams,
  getAssessment,
  getAssessmentProgress,
  getCalculatedFields,
  getAssessmentAnsweredQuestions,
  getAssessmentTotalQuestions,
  getAssessmentParticipants,
  getAssessmentParticipantsMetadata,
  getAssessmentCategories,
  getAssessmentQuestions,
  getAssessmentOwners,
  getAssessmentStatus,
  getAssessmentHiddenFields,
  getAssessmentParticipantTableData,
  getDDERequiredFields,
  getActiveUsersEmails,
  getUpdatedCategoryListAfterFilter
};
